import { Divider, IconButton, Stack, TextField, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';

import {
  useGetIntegratorMeQuery,
  useRotateIntegratorWebhookSecretMutation,
  useUpdateIntegratorCredentialsMutation,
} from '@/api/v1';
import CopyIcon from '@/assets/icons/copy.svg?react';
import EyeClosedIcon from '@/assets/icons/eye-closed.svg?react';
import EyeOpenIcon from '@/assets/icons/eye-open.svg?react';
import { TabPaper } from '@/components/atoms/TabPaper';
import { ConfirmationModal } from '@/components/molecules/ConfirmationModal';
import { LoadingButton } from '@/components/molecules/LoadingButton';
import { useToast } from '@/hooks/useToast';
import { useTranslator } from '@/hooks/useTranslator';

const MASK = '••••••••••••••••••••';

interface SecretRowProps {
  readonly label: string;
  readonly value: string;
}

const SecretRow: React.FC<SecretRowProps> = ({
  label,
  value,
}: SecretRowProps): React.ReactElement => {
  const tr = useTranslator();
  const toast = useToast();
  const [revealed, setRevealed] = React.useState<boolean>(false);

  const onToggle = React.useCallback((): void => {
    setRevealed((prev: boolean): boolean => !prev);
  }, []);

  const onCopy = React.useCallback((): void => {
    navigator.clipboard.writeText(value).then(
      (): void => {
        toast.show(tr('COPIED'), 'success');
      },
      (): void => {
        toast.show(tr('AN_ERROR_OCCURRED'), 'error');
      },
    );
  }, [value, toast, tr]);

  return (
    <>
      <Typography variant="body2" color="custom.LightGray">
        {label}
      </Typography>
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
        <Typography variant="body2" sx={styles.secretText}>
          {revealed ? value : MASK}
        </Typography>
        <Stack direction="row" alignItems="center">
          <IconButton
            color="inherit"
            size="medium"
            onClick={onToggle}
            aria-label={revealed ? tr('HIDE') : tr('REVEAL')}>
            {revealed ? (
              <EyeClosedIcon fill="currentColor" width={16} height={16} />
            ) : (
              <EyeOpenIcon fill="currentColor" width={16} height={16} />
            )}
          </IconButton>
          <IconButton color="inherit" size="medium" onClick={onCopy} aria-label={tr('COPY')}>
            <CopyIcon fill="currentColor" width={16} height={16} />
          </IconButton>
        </Stack>
      </Stack>
    </>
  );
};

const parseEmails = (value: string): readonly string[] =>
  value
    .split(',')
    .map((s: string): string => s.trim())
    .filter((s: string): boolean => s.length > 0);

export const SettingsApiCredentialsTab: React.FC = (): React.ReactElement => {
  const tr = useTranslator();
  const toast = useToast();

  const { data, isLoading } = useGetIntegratorMeQuery();
  const [updateCredentials, { isLoading: saving }] = useUpdateIntegratorCredentialsMutation();
  const [rotateSecret, { isLoading: rotating }] = useRotateIntegratorWebhookSecretMutation();

  const [callbackUrl, setCallbackUrl] = React.useState<string>('');
  const [contactEmailsInput, setContactEmailsInput] = React.useState<string>('');
  const [confirmRotateOpen, setConfirmRotateOpen] = React.useState<boolean>(false);

  React.useEffect((): void => {
    if (data) {
      setCallbackUrl(data.callbackUrl ?? '');
      setContactEmailsInput((data.contactEmails ?? []).join(', '));
    }
  }, [data]);

  const onCallbackUrlChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      setCallbackUrl(event.target.value);
    },
    [],
  );

  const onContactEmailsChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      setContactEmailsInput(event.target.value);
    },
    [],
  );

  const handleSave = React.useCallback(async (): Promise<void> => {
    try {
      await updateCredentials({
        callbackUrl: callbackUrl,
        contactEmails: parseEmails(contactEmailsInput),
      }).unwrap();
      toast.show(tr('API_CREDENTIALS_UPDATE_SUCCESS'), 'success');
    } catch {
      toast.show(tr('API_CREDENTIALS_UPDATE_ERROR'), 'error');
    }
  }, [callbackUrl, contactEmailsInput, updateCredentials, toast, tr]);

  const openRotateConfirm = React.useCallback((): void => {
    setConfirmRotateOpen(true);
  }, []);

  const closeRotateConfirm = React.useCallback((): void => {
    setConfirmRotateOpen(false);
  }, []);

  const handleRotateConfirm = React.useCallback(async (): Promise<void> => {
    try {
      await rotateSecret().unwrap();
      toast.show(tr('WEBHOOK_SECRET_ROTATED'), 'success');
    } catch {
      toast.show(tr('WEBHOOK_SECRET_ROTATE_ERROR'), 'error');
    } finally {
      setConfirmRotateOpen(false);
    }
  }, [rotateSecret, toast, tr]);

  if (isLoading || !data) {
    return <TabPaper>{null}</TabPaper>;
  }

  const clientId = data.credentials?.clientId ?? '';
  const clientSecret = data.credentials?.clientSecret ?? '';
  const webhookSecret = data.webhookSecret ?? '';

  return (
    <TabPaper>
      <Box display="flex" flexDirection="column" gap={5}>
        <Box>
          <Typography variant="h7" component="h6">
            {tr('API_CREDENTIALS_KEYS_SECTION_LABEL')}
          </Typography>
          <Typography variant="body2" color="custom.LightGray" mt={1}>
            {tr('API_CREDENTIALS_KEYS_SECTION_DESCRIPTION')}
          </Typography>
          <Box sx={styles.grid} mt={3}>
            <Typography variant="body2" color="custom.LightGray">
              {tr('CLIENT_ID')}
            </Typography>
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
              <Typography variant="body2" sx={styles.secretText}>
                {clientId}
              </Typography>
              <IconButton
                color="inherit"
                size="medium"
                aria-label={tr('COPY')}
                onClick={(): void => {
                  navigator.clipboard.writeText(clientId).then(
                    (): void => toast.show(tr('COPIED'), 'success'),
                    (): void => toast.show(tr('AN_ERROR_OCCURRED'), 'error'),
                  );
                }}>
                <CopyIcon fill="currentColor" width={16} height={16} />
              </IconButton>
            </Stack>
            <SecretRow label={tr('CLIENT_SECRET')} value={clientSecret} />
            <SecretRow label={tr('WEBHOOK_SECRET')} value={webhookSecret} />
          </Box>
          <Box mt={3}>
            <LoadingButton
              variant="light"
              size="compact"
              loading={rotating}
              onClick={openRotateConfirm}>
              {tr('ROTATE_WEBHOOK_SECRET')}
            </LoadingButton>
          </Box>
        </Box>

        <Divider />

        <Box>
          <Typography variant="h7" component="h6">
            {tr('API_CREDENTIALS_WEBHOOK_SECTION_LABEL')}
          </Typography>
          <Typography variant="body2" color="custom.LightGray" mt={1}>
            {tr('API_CREDENTIALS_WEBHOOK_SECTION_DESCRIPTION')}
          </Typography>
          <Box width={520} mt={3}>
            <TextField
              label={tr('CALLBACK_URL')}
              fullWidth={true}
              value={callbackUrl}
              onChange={onCallbackUrlChange}
            />
          </Box>
          <Box width={520} mt={3}>
            <TextField
              label={tr('CONTACT_EMAILS')}
              helperText={tr('CONTACT_EMAILS_HELPER')}
              fullWidth={true}
              value={contactEmailsInput}
              onChange={onContactEmailsChange}
            />
          </Box>
          <Box mt={3}>
            <LoadingButton variant="dark" loading={saving} onClick={handleSave}>
              {tr('SAVE_CHANGES')}
            </LoadingButton>
          </Box>
        </Box>
      </Box>

      <ConfirmationModal
        open={confirmRotateOpen}
        title={tr('ROTATE_WEBHOOK_SECRET_CONFIRM_TITLE')}
        description={tr('ROTATE_WEBHOOK_SECRET_CONFIRM_DESCRIPTION')}
        confirmButtonText={tr('ROTATE')}
        abortButtonText={tr('CANCEL')}
        loading={rotating}
        onConfirm={handleRotateConfirm}
        onAbort={closeRotateConfirm}
      />
    </TabPaper>
  );
};

const styles: Record<string, SystemStyleObject> = {
  grid: {
    display: 'grid',
    gridTemplateColumns: '160px 1fr',
    alignItems: 'center',
    rowGap: 3,
    maxWidth: 720,
  },
  secretText: {
    fontFamily: 'monospace',
    overflowWrap: 'anywhere',
    flex: 1,
  },
};
